// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";

import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";
import { getEnergySourceName, getSiteSelection, toDropdownItems } from "guiutils";
import { EnergySource, IEnergyMeter, ITariffCircuit } from "shared/types/parameter";
import { IState } from "states/states";
import TariffCircuitListModel from "model/tariffcircuitlist";

import Dropdown from "view/comp/Dropdown";

interface IEnergySourceSelectExtProps {
  allowAllEnergySources: boolean;
  className?: string;
  emptyText?: JSX.Element;
}

interface IEnergySourceSelectProps {
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selectedEnergySource: EnergySource | undefined;
}

const mapStateToProps = (state: IState, ownProps: IEnergySourceSelectExtProps) => {
  const selection = getSiteSelection(state);
  const props: IEnergySourceSelectProps = {
    tariffCircuits: state.para.parameter?.tariffline ?? [],
    energyMeters: state.para.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
  };
  return props;
};

interface IEnergySourceSelectDispatch {
  onEnergySourceChanged: (selection: EnergySource | undefined) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnergySourceSelectDispatch = {
    onEnergySourceChanged: (selection: EnergySource | undefined) => {
      const payload: IActionTypeSelectGuiItem = { selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_ENERGYSOURCE, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCaption: {
    id: "Select energy source",
  },
});

class EnergySourceSelectComponent extends React.Component<
  IEnergySourceSelectProps & IEnergySourceSelectExtProps & IEnergySourceSelectDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    // in pie-chart view mode, we must not switch to all energy sources
    const energySources = tcModel.getEnergySources(this.props.allowAllEnergySources);
    const sourceItems = toDropdownItems(
      energySources,
      "selectedEnergySource",
      (name, value) => this.props.onEnergySourceChanged(value),
      (value) =>
        value ? getEnergySourceName(value) : <FormattedMessage id="All energy sources" />,
    );
    const hasMultipleEnergySources =
      this.props.allowAllEnergySources ? energySources.length > 2 : energySources.length > 1;

    return (
      hasMultipleEnergySources ?
        <div id="energysourceselect" className={this.props.className}>
          <Dropdown
            style={{
              marginTop: 0,
              backgroundColor: "transparent",
              border: "1px solid transparent",
            }}
            isHeaderStyle={true}
            items={sourceItems}
            caption={this.context.formatMessage(messages.selectCaption)}
            curItem={
              this.props.selectedEnergySource || !this.props.allowAllEnergySources ?
                getEnergySourceName(this.props.selectedEnergySource)
              : <FormattedMessage id="All energy sources" />
            }
          />
        </div>
      : this.props.emptyText ?
        <div id="energysourceselect" className={this.props.className}>
          {this.props.emptyText}
        </div>
      : <></>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EnergySourceSelectComponent);
